import { useMemo, useRef, useState } from 'react';
import { Check, ClipboardText, TerminalWindow } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { Button } from './Button';
import { Modal } from './Modal';
import { buildAwsCliSnippet, type AwsCliSnippetInput } from '@/lib/awsConfig/snippets';
import { cn } from '@/lib/utils/cn';

interface AwsCliConfigModalProps {
  open: boolean;
  onClose: () => void;
  input: AwsCliSnippetInput | null;
}

const SETUP_STEPS = [
  'Copie a config',
  'Cole no ~/.aws/config',
  'Faça login SSO',
  'Teste o profile',
];

export function AwsCliConfigModal({ open, onClose, input }: AwsCliConfigModalProps) {
  const [copiedConfig, setCopiedConfig] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);
  const commandCopyTimeoutRef = useRef<number | null>(null);
  const snippetState = useMemo(() => {
    if (!open || !input) return { snippet: null, error: null };
    try {
      return { snippet: buildAwsCliSnippet(input), error: null };
    } catch {
      return {
        snippet: null,
        error: 'Não foi possível gerar a config AWS CLI para esta solicitação.',
      };
    }
  }, [input, open]);
  const { snippet, error } = snippetState;
  const sections = useMemo(() => splitConfigSections(snippet?.config), [snippet?.config]);

  const copyText = async (value: string, label: string) => {
    try {
      await window.navigator.clipboard.writeText(value);
      if (label === 'config') {
        setCopiedConfig(true);
        window.setTimeout(() => setCopiedConfig(false), 1800);
      } else {
        if (commandCopyTimeoutRef.current) {
          window.clearTimeout(commandCopyTimeoutRef.current);
        }
        setCopiedCommand(label);
        commandCopyTimeoutRef.current = window.setTimeout(() => {
          setCopiedCommand((current) => (current === label ? null : current));
          commandCopyTimeoutRef.current = null;
        }, 1800);
      }
    } catch {
      toast.error('Não consegui copiar automaticamente.', {
        description: 'Selecione o texto e copie manualmente.',
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="AWS CLI config" size="lg">
      {error && (
        <div className="rounded-xl border border-border bg-surface-muted/40 p-4 text-[0.8125rem] leading-relaxed text-muted">
          {error} Recarregue a página e tente novamente. Se continuar, peça para um admin validar o
          e-mail do solicitante no grant.
        </div>
      )}
      {snippet && input && (
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-surface-muted/40 p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 rounded-lg bg-accent/10 p-2 text-accent">
                <TerminalWindow size={18} weight="bold" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-ink">
                  Configure uma vez. Use quando o acesso estiver ativo.
                </h3>
                <p className="mt-1 text-[0.8125rem] leading-relaxed text-muted">
                  Cole os blocos no seu <code>~/.aws/config</code>. Eles salvam apenas metadata de
                  profile, não credenciais nem tokens.
                </p>
              </div>
            </div>
          </div>

          <ol className="grid gap-2 sm:grid-cols-4" aria-label="Passos para configurar AWS CLI">
            {SETUP_STEPS.map((step, index) => (
              <li
                key={step}
                className={cn(
                  'rounded-lg border px-3 py-2.5 text-[0.75rem] leading-snug transition-colors',
                  copiedConfig && index === 0
                    ? 'border-success/40 bg-success/10 text-success'
                    : 'border-border bg-surface text-muted',
                )}
              >
                <span className="mb-1 block text-[0.6875rem] font-semibold uppercase tracking-[0.14em] opacity-70">
                  {index + 1}
                </span>
                <span className="font-medium">
                  {copiedConfig && index === 0 ? 'Copiado' : step}
                </span>
              </li>
            ))}
          </ol>

          <div className="rounded-xl border border-border bg-surface p-3 sm:p-4">
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted">
                  Profile gerado
                </p>
                <p className="mt-1 break-all font-mono text-[0.8125rem] text-ink">
                  {snippet.profileName}
                </p>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="min-h-11 sm:min-h-0"
                onClick={() => void copyText(snippet.config, 'config')}
                aria-label={copiedConfig ? 'Config copiada' : 'Copiar config AWS CLI'}
              >
                {copiedConfig ? (
                  <Check size={13} weight="bold" aria-hidden="true" />
                ) : (
                  <ClipboardText size={13} weight="bold" aria-hidden="true" />
                )}
                {copiedConfig ? 'Copiado' : 'Copiar config'}
              </Button>
            </div>

            <div className="space-y-3">
              <ConfigCodeBlock label="Base SSO" code={sections.sso} />
              <ConfigCodeBlock label="Profile Aura Jumper" code={sections.jumper} />
              <ConfigCodeBlock label="Profile de destino" code={sections.target} />
            </div>
          </div>

          <div className="rounded-xl border border-border bg-surface-muted/30 px-4 py-3 text-[0.8125rem] leading-relaxed text-muted">
            {input.kind === 'jit' ? (
              <>
                O profile pode ficar no arquivo. O acesso JIT só funciona enquanto este grant do
                TEAM estiver ativo; se expirar ou for revogado, abra uma nova solicitação antes de
                usar novamente.
              </>
            ) : (
              <>
                Este profile assume <code>role/aura</code> e permanece ReadOnly. Para escrita, use
                uma solicitação JIT aprovada.
              </>
            )}
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <CommandCard
              label="Login SSO"
              command={snippet.loginCommand}
              copied={copiedCommand === 'login'}
              onCopy={() => void copyText(snippet.loginCommand, 'login')}
            />
            <CommandCard
              label="Testar profile"
              command={snippet.testCommand}
              copied={copiedCommand === 'test'}
              onCopy={() => void copyText(snippet.testCommand, 'test')}
            />
          </div>

          <p className="text-[0.75rem] leading-relaxed text-muted">
            Se você já tiver estes blocos, substitua as seções correspondentes{' '}
            <code>[sso-session aura]</code>, <code>[profile aura-jumper]</code> ou o profile de
            destino gerado.
          </p>
        </div>
      )}
    </Modal>
  );
}

function ConfigCodeBlock({ label, code }: { label: string; code: string }) {
  return (
    <section aria-label={label}>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-muted">
          {label}
        </p>
      </div>
      <pre className="max-h-[220px] overflow-auto rounded-lg border border-ink/10 bg-ink p-3 text-[0.75rem] leading-relaxed text-white shadow-inner sm:p-4">
        <code>{code}</code>
      </pre>
    </section>
  );
}

function CommandCard({
  label,
  command,
  copied,
  onCopy,
}: {
  label: string;
  command: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="rounded-xl border border-border bg-surface-muted/40 p-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-muted">
          {label}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          className="min-h-11 px-3 sm:min-h-8 sm:px-2"
          onClick={onCopy}
          aria-label={copied ? `${label} copiado` : `Copiar comando ${label}`}
        >
          {copied ? (
            <Check size={12} weight="bold" aria-hidden="true" />
          ) : (
            <ClipboardText size={12} weight="bold" aria-hidden="true" />
          )}
          {copied ? 'Copiado' : 'Copiar'}
        </Button>
      </div>
      <code className="block break-all font-mono text-[0.75rem] leading-relaxed text-ink">
        {command}
      </code>
    </div>
  );
}

function splitConfigSections(config?: string) {
  if (!config) {
    return { sso: '', jumper: '', target: '' };
  }

  const blocks = config.split('\n\n');
  const sso = blocks.find((block) => block.startsWith('[sso-session ')) ?? '';
  const jumper = blocks.find((block) => block.startsWith('[profile aura-jumper]')) ?? '';
  const target = blocks.find((block) => block.startsWith('[profile ') && block !== jumper) ?? '';

  return { sso, jumper, target };
}
